import type {
  BreedSize,
  FoodType,
  LifeStage,
  PetType,
  ProductLine,
  SpecialNeed,
  StockStatus,
} from '@/types/catalog';

/**
 * Every domain enum gets exactly one Portuguese label here. Components read
 * from these maps instead of inlining strings, so a wording change is one edit
 * and filter chips can never disagree with product cards.
 */

interface TermMeta {
  label: string;
  /** Shorter form for chips and dense filter rows. */
  short?: string;
  emoji?: string;
}

export const PET_TYPES: Record<PetType, TermMeta> = {
  dog: { label: 'Cães', short: 'Cão', emoji: '🐶' },
  cat: { label: 'Gatos', short: 'Gato', emoji: '🐱' },
};

export const LIFE_STAGES: Record<LifeStage, TermMeta> = {
  puppy: { label: 'Filhote', emoji: '🍼' },
  adult: { label: 'Adulto', emoji: '🦴' },
  senior: { label: 'Sênior', emoji: '💛' },
};

export const BREED_SIZES: Record<BreedSize, TermMeta> = {
  small: { label: 'Porte pequeno', short: 'Pequeno' },
  medium: { label: 'Porte médio', short: 'Médio' },
  large: { label: 'Porte grande', short: 'Grande' },
};

export const PRODUCT_LINES: Record<ProductLine, TermMeta> = {
  standard: { label: 'Padrão', short: 'Padrão' },
  premium: { label: 'Premium', short: 'Premium' },
  'super-premium': { label: 'Super Premium', short: 'Super Prem.' },
};

export const FOOD_TYPES: Record<FoodType, TermMeta> = {
  dry: { label: 'Ração seca', short: 'Seca', emoji: '🥣' },
  wet: { label: 'Sachê e úmido', short: 'Úmido', emoji: '🍲' },
  treat: { label: 'Petiscos', short: 'Petisco', emoji: '🦴' },
  supplement: { label: 'Complementos', short: 'Complemento', emoji: '💊' },
  hygiene: { label: 'Higiene (areias e tapetes)', short: 'Higiene', emoji: '🧻' },
};

export const SPECIAL_NEEDS: Record<SpecialNeed, TermMeta> = {
  castrated: { label: 'Castrados' },
  sensitive: { label: 'Digestão sensível' },
  indoor: { label: 'Ambiente interno' },
  'weight-control': { label: 'Controle de peso' },
  hairball: { label: 'Controle de bolas de pelo' },
  urinary: { label: 'Saúde urinária' },
  'skin-coat': { label: 'Pele e pelagem' },
  natural: { label: 'Natural / sem corantes' },
};

export const STOCK_STATUS: Record<StockStatus, TermMeta & { tone: 'ok' | 'warn' | 'off' }> = {
  'in-stock': { label: 'Em estoque', tone: 'ok' },
  'low-stock': { label: 'Últimas unidades', tone: 'warn' },
  'out-of-stock': { label: 'Sob encomenda', tone: 'off' },
};

/** Turns a record into the `{ value, label }` shape filter UIs consume. */
export function toOptions<K extends string>(
  record: Record<K, TermMeta>,
): Array<{ value: K; label: string; emoji?: string }> {
  return (Object.keys(record) as K[]).map((value) => ({
    value,
    label: record[value].label,
    ...(record[value].emoji ? { emoji: record[value].emoji } : {}),
  }));
}
